import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, ShieldOff, Copy, Check, Loader2, KeyRound } from "lucide-react";

export default function MfaSettings() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // { secret, provisioning_uri, qr_data_url }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/enable", { code });
      setRecovery(data.recovery_codes);
      setSetup(null); setCode("");
      load(); refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/mfa/disable", { password: disablePw, code: disableCode });
      setDisablePw(""); setDisableCode("");
      load(); refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((recovery || []).join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!status) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Two-factor authentication</h1>
        <p className="text-sm text-slate-600">Add a second layer of protection to your Rabah Concept account using an authenticator app.</p>
      </header>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm" data-testid="mfa-error">{error}</div>}

      {recovery && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="mfa-recovery-codes">
          <div className="flex items-center gap-2 text-amber-900">
            <KeyRound className="h-5 w-5" />
            <h3 className="font-heading text-lg font-semibold">Save your recovery codes</h3>
          </div>
          <p className="text-sm text-amber-900 mt-1">Each code can be used <strong>once</strong> if you lose access to your authenticator. They will not be shown again.</p>
          <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
            {recovery.map((c) => (
              <li key={c} className="bg-white border border-amber-200 rounded-md px-3 py-2 tracking-wider">{c}</li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2">
            <button onClick={copyCodes} className="inline-flex items-center gap-2 bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 px-3 py-1.5 rounded-md text-sm">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy all"}
            </button>
            <button onClick={() => setRecovery(null)} className="text-sm px-3 py-1.5">Done</button>
          </div>
        </div>
      )}

      {!status.mfa_enabled && !setup && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cc-glow">
          <div className="flex items-center gap-2 mb-2">
            <ShieldOff className="h-5 w-5 text-slate-500" />
            <h3 className="font-heading text-lg font-semibold text-slate-900">MFA is not enabled</h3>
          </div>
          {status.mfa_setup_required && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3" data-testid="mfa-required-notice">
              MFA is required for management roles per organisation policy. Please enrol now.
            </p>
          )}
          <p className="text-sm text-slate-600 mb-4">You will need an authenticator app — Google Authenticator, Microsoft Authenticator, Authy or 1Password all work.</p>
          <button onClick={startSetup} disabled={busy} data-testid="mfa-start-setup-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} <ShieldCheck className="h-4 w-4" /> Set up MFA
          </button>
        </div>
      )}

      {setup && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cc-glow space-y-4" data-testid="mfa-setup-panel">
          <h3 className="font-heading text-lg font-semibold text-slate-900">Scan this QR code</h3>
          <p className="text-sm text-slate-600">Open your authenticator app and scan the code below. If you can't scan, enter the secret manually.</p>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <img src={setup.qr_data_url} alt="MFA QR code" className="w-44 h-44 border border-slate-200 rounded-md bg-white" data-testid="mfa-qr-image" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-slate-500">Manual entry secret</p>
              <p className="font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-2 mt-1 break-all" data-testid="mfa-secret-text">{setup.secret}</p>
              <p className="text-xs text-slate-500 mt-1">Account: {user?.email} · Issuer: Rabah Concept</p>
            </div>
          </div>
          <form onSubmit={enable} className="space-y-3 pt-2 border-t border-slate-100">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Enter 6-digit code from your app</label>
              <input
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                data-testid="mfa-enable-code-input"
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white text-center font-mono tracking-widest text-lg"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setSetup(null); setCode(""); }} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="mfa-enable-button" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify &amp; enable MFA
              </button>
            </div>
          </form>
        </div>
      )}

      {status.mfa_enabled && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cc-glow space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">MFA is enabled</h3>
                <p className="text-xs text-slate-500">{status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining !== 1 && "s"} remaining</p>
              </div>
            </div>
          </div>
          <details className="border-t border-slate-100 pt-3">
            <summary className="text-sm text-red-700 cursor-pointer">Disable MFA</summary>
            <form onSubmit={disable} className="mt-3 space-y-2" data-testid="mfa-disable-form">
              <input type="password" required placeholder="Your password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md" />
              <input required placeholder="Current 6-digit code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md font-mono tracking-widest text-center" />
              <button type="submit" disabled={busy} data-testid="mfa-disable-button" className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md font-medium">
                Disable MFA
              </button>
            </form>
          </details>
        </div>
      )}
    </div>
  );
}
